import { useEffect, useRef } from "preact/hooks";

/**
 * Escape and Android's back close the top layer (docs/product.md: closing by backdrop tap, Android
 * back, iOS back swipe, Escape and the Close button all count as a close). Mount `useEscape` in
 * every sheet, prompt, viewer and the tour with `active` true while the layer is open. Layers form
 * a stack in the order they opened, and only the top one answers, so a tour over the account sheet
 * closes one layer per press. A layer keeps its place in the stack while it re-renders: the latest
 * `onEscape` is read through a ref, so an inline arrow never moves it to the top.
 */
const layers: Array<() => void> = [];
let listening = false;

/**
 * Closes the top layer, if there is one, and says whether it did: Android's back button
 * (src/native-android.ts) closes a sheet or viewer before it goes up a screen.
 */
export function closeTopLayer(): boolean {
  const top = layers[layers.length - 1];
  if (!top) return false;
  top();
  return true;
}

/** How many layers are open, for tests. */
export function openLayerCount(): number {
  return layers.length;
}

function onKey(e: KeyboardEvent): void {
  if (e.key !== "Escape" || e.defaultPrevented) return;
  if (layers.length === 0) return;
  e.preventDefault();
  closeTopLayer();
}

/** Puts a layer on top of the stack; the function it returns takes it off. Exported apart from the hook for tests. */
export function pushLayer(onEscape: () => void): () => void {
  const entry = () => onEscape();
  layers.push(entry);
  if (!listening && typeof window !== "undefined") {
    window.addEventListener("keydown", onKey);
    listening = true;
  }
  return () => {
    const i = layers.lastIndexOf(entry);
    if (i >= 0) layers.splice(i, 1);
    if (layers.length === 0 && listening) {
      window.removeEventListener("keydown", onKey);
      listening = false;
    }
  };
}

export function useEscape(active: boolean, onEscape: () => void): void {
  const latest = useRef(onEscape);
  latest.current = onEscape;
  useEffect(() => {
    if (!active) return;
    return pushLayer(() => latest.current());
  }, [active]);
}
